/*
 * Copyright 2026 North Carolina State University
 */

import { formatClock } from '../../utils/timeFormat';

/**
 * The field types an admin can put on an IPM form, and how each one's stored
 * value is shown. Must match FIELD_TYPES in backend/modules/ipm/routes.js.
 */

export const FIELD_TYPES = [
  { type: 'text', label: 'Short text', width: 'half' },
  { type: 'textarea', label: 'Long text', width: 'full' },
  { type: 'number', label: 'Number', width: 'half', hasUnits: true },
  { type: 'date', label: 'Date', width: 'half', hasDefaultToday: true },
  { type: 'time', label: 'Time', width: 'half', hasDefaultToday: true },
  { type: 'boolean', label: 'Yes / No', width: 'half' },
  { type: 'select', label: 'Dropdown (pick one)', width: 'half', hasOptions: true },
  { type: 'multiselect', label: 'Dropdown (pick many)', width: 'half', hasOptions: true },
  { type: 'checkboxes', label: 'Checkboxes', width: 'full', hasOptions: true },
  { type: 'chamber', label: 'Compartment / room', width: 'half', hasMultiple: true },
  { type: 'project', label: 'Project', width: 'half', hasMultiple: true },
  { type: 'person', label: 'Person (from app users)', width: 'half', hasMultiple: true, hasRoles: true },
  { type: 'control', label: 'Control (from List of Controls)', width: 'half', hasMultiple: true },
  { type: 'tags', label: 'List (type several values)', width: 'full', hasSuggestions: true },
  { type: 'images', label: 'Images', width: 'full' },
  { type: 'files', label: 'Documents / files', width: 'full' },
  { type: 'table', label: 'Table (repeating rows)', width: 'full', hasColumns: true },
];

export const COLUMN_TYPES = [
  { type: 'text', label: 'Text' },
  { type: 'number', label: 'Number', hasUnits: true },
  { type: 'select', label: 'Dropdown', hasOptions: true },
  { type: 'date', label: 'Date' },
  { type: 'time', label: 'Time' },
  { type: 'species', label: 'Species (from species list)' },
  { type: 'control', label: 'Control (from List of Controls)' },
];

export const typeInfo = (type) => FIELD_TYPES.find(t => t.type === type) || { type, label: type };
export const columnTypeInfo = (type) => COLUMN_TYPES.find(t => t.type === type) || COLUMN_TYPES[0];

/** A permanent key for a new field/section/column. Never shown, never changed. */
export const newId = (prefix = 'f') =>
  `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export const isBlank = (v) => {
  if (v === undefined || v === null || v === '') return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object' && 'value' in v) return v.value === null || v.value === '' || v.value === undefined;
  return false;
};

const fmtDate = (s) => {
  if (!s || typeof s !== 'string') return '';
  const [y, m, d] = s.split('-');
  return y && m && d ? `${m}/${d}/${y}` : s;
};

// Stored as "HH:mm"; shown in the user's 12h/24h choice.
const fmtTime = (s) => (!s || typeof s !== 'string' ? '' : formatClock(s));

const fmtNumber = (v) => {
  if (v && typeof v === 'object') return [v.value, v.unit].filter(x => x !== undefined && x !== null && x !== '').join(' ');
  return v === undefined || v === null ? '' : String(v);
};

const names = (v) => (Array.isArray(v) ? v : [v]).filter(Boolean).map(x => x.name || x.label || '').filter(Boolean).join(', ');

/** Plain-text form of a stored value. Tables and images get richer views where shown. */
export const formatValue = (field, value) => {
  if (isBlank(value)) return '';
  switch (field.type) {
    case 'date': return fmtDate(value);
    case 'time': return fmtTime(value);
    case 'number': return fmtNumber(value);
    case 'boolean': return value === true ? 'Yes' : value === false ? 'No' : String(value);
    case 'multiselect':
    case 'tags':
    case 'checkboxes': return Array.isArray(value) ? value.join(', ') : String(value);
    case 'chamber':
    case 'project':
    case 'person':
    case 'control':
    case 'species': return names(value);
    case 'images': return `${Array.isArray(value) ? value.length : 0} image(s)`;
    case 'files': return names(value);
    case 'table': {
      // The first column usually names the row (a product, a species), so list those.
      const first = field.columns?.[0];
      if (!Array.isArray(value)) return '';
      const labels = first ? value.map(r => formatValue(first, r[first.id])).filter(Boolean) : [];
      return labels.length ? labels.join(', ') : `${value.length} row(s)`;
    }
    default: return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }
};

export const formatCell = (column, value) => formatValue(column, value);

export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const nowHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export const formFields = (form) => (form?.sections || []).flatMap(s => s.fields.map(f => ({ ...f, section: s.title })));
